/**
 * Grid: the one table. Columns are declared once as TanStack column
 * definitions; React Aria's Table gives the rows keyboard navigation
 * and an accessible grid; every cell renders a stratum or text. Every
 * row renders -- scope is the data budget, so no grid here ever needs
 * to virtualise (docs/STRATA.md law 5).
 */
import type { ReactNode } from 'react';
import {
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components';
import {
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { CONTROL, SPACE, TEXT, TRACKING, WEIGHT, usePalette } from '../theme';

/**
 * The one feature set every grid registers (TanStack Table v9 bundles
 * nothing by default): sorting and its row model. Whether a given grid
 * sorts is `enableSorting`, never a different feature set.
 */
const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
});

export type GridFeatures = typeof features;

export type GridColumn<Row extends RowData> = ColumnDef<GridFeatures, Row, unknown> & {
  /** A fixed width in px; unset columns share the rest. */
  width?: number;
  /** Right-align (numbers). */
  numeric?: boolean;
};

export function Grid<R extends RowData>({
  rows,
  columns,
  rowKey,
  ariaLabel,
  empty,
  sortable = false,
}: {
  rows: readonly R[];
  columns: readonly GridColumn<R>[];
  rowKey: (row: R) => string;
  ariaLabel: string;
  /** What to show when there are no rows. */
  empty?: ReactNode;
  sortable?: boolean;
}) {
  const p = usePalette();
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useTable({
    features,
    data: rows as R[],
    columns: columns as ColumnDef<GridFeatures, R, unknown>[],
    state: { sorting },
    onSortingChange: setSorting,
    enableSorting: sortable,
    getRowId: (row) => rowKey(row),
  });
  const widthOf = (id: string) => columns.find((c) => c.id === id)?.width;
  const numericOf = (id: string) => columns.find((c) => c.id === id)?.numeric ?? false;

  if (rows.length === 0 && empty) {
    return (
      <div style={{ padding: SPACE.lg, textAlign: 'center', color: p.inkMuted, fontSize: TEXT.control }}>{empty}</div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', minWidth: 0 }}>
      <Table
        aria-label={ariaLabel}
        style={{ borderCollapse: 'collapse', width: '100%', fontSize: TEXT.control, color: p.ink }}
      >
        <TableHeader>
          {table.getFlatHeaders().map((header) => (
            <Column
              key={header.id}
              id={header.id}
              isRowHeader={header.index === 0}
              allowsSorting={sortable && header.column.getCanSort()}
              width={widthOf(header.column.id)}
              style={{
                textAlign: numericOf(header.column.id) ? 'right' : 'left',
                fontWeight: WEIGHT.strong,
                color: p.inkMuted,
                fontSize: TEXT.micro,
                letterSpacing: TRACKING.label,
                textTransform: 'uppercase',
                padding: `${CONTROL.padYHeader}px ${CONTROL.padXCompact}px`,
                borderBottom: `1px solid ${p.hairline}`,
                cursor: sortable && header.column.getCanSort() ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                outline: 'none',
              }}
            >
              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            </Column>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <Row
              key={row.id}
              id={row.id}
              style={({ isHovered, isFocusVisible }) => ({
                background: isHovered ? p.surfaceRaised : 'transparent',
                outline: isFocusVisible ? `2px solid ${p.blue}` : 'none',
                outlineOffset: -2,
              })}
            >
              {row.getAllCells().map((cell) => (
                <Cell
                  key={cell.id}
                  style={{
                    padding: `${CONTROL.padYCell}px ${CONTROL.padXCompact}px`,
                    borderBottom: `1px solid ${p.hairline}`,
                    verticalAlign: 'top',
                    textAlign: numericOf(cell.column.id) ? 'right' : 'left',
                    fontVariantNumeric: numericOf(cell.column.id) ? 'tabular-nums' : undefined,
                    outline: 'none',
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Cell>
              ))}
            </Row>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
